/**
 * Koji Logo Marquee
 * Custom element <koji-marquee>: si inizializza da solo ad ogni render della section
 * (anche nel theme editor) e si può caricare più volte senza effetti collaterali.
 *
 * Come funziona il loop:
 *  - .koji-marquee-track è l'UNICO elemento animato (CSS: koji-scroll).
 *  - La traslazione è pari alla larghezza esatta dell'elenco originale (--koji-shift),
 *    quindi originale e cloni restano sempre sincronizzati.
 *  - La durata è calcolata da larghezza / velocità (px al secondo), così la velocità
 *    non cambia se si aggiungono o tolgono loghi (--koji-duration).
 */
(function () {
  if (customElements.get('koji-marquee')) return;

  class KojiMarquee extends HTMLElement {
    connectedCallback() {
      this.container = this.querySelector('.koji-marquee-container');
      this.track = this.querySelector('.koji-marquee-track');
      this.original = this.querySelector('.koji-marquee-content-original');
      if (!this.container || !this.track || !this.original) return;

      this.speed = Math.max(parseFloat(this.dataset.speed) || 40, 1);
      this.lastContentWidth = 0;
      this.lastContainerWidth = 0;
      this.frame = 0;
      this.onChange = () => this.schedule();

      // Ricostruisce solo se cambia la LARGHEZZA (la barra URL del mobile cambia solo l'altezza)
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onChange);
        this.resizeObserver.observe(this.container);
        this.resizeObserver.observe(this.original);
      } else {
        window.addEventListener('resize', this.onChange);
      }

      // Ferma l'animazione quando la section è fuori schermo (batteria / CPU)
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.dataset.offscreen = entries[0].isIntersecting ? 'false' : 'true';
        });
        this.visibilityObserver.observe(this);
      }

      // Pulsante pausa / riprendi (WCAG 2.2.2)
      this.toggleButton = this.querySelector('.koji-marquee-toggle');
      if (this.toggleButton) {
        this.onToggle = () => this.setPaused(this.dataset.paused !== 'true');
        this.toggleButton.addEventListener('click', this.onToggle);
        this.updateToggle();
      }

      this.build();
    }

    disconnectedCallback() {
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      window.removeEventListener('resize', this.onChange);
      if (this.toggleButton && this.onToggle) {
        this.toggleButton.removeEventListener('click', this.onToggle);
      }
    }

    schedule() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.build();
      });
    }

    build() {
      // Larghezza frazionaria: evita micro-scatti ad ogni giro del loop
      const contentWidth = this.original.getBoundingClientRect().width;
      const containerWidth = this.container.clientWidth;
      if (!contentWidth || !containerWidth) return;

      if (contentWidth === this.lastContentWidth && containerWidth === this.lastContainerWidth) return;
      this.lastContentWidth = contentWidth;
      this.lastContainerWidth = containerWidth;

      // Copie totali (1 + cloni) devono coprire contenitore + una larghezza: cloni >= contenitore / contenuto
      const clonesNeeded = Math.max(1, Math.ceil(containerWidth / contentWidth));
      this.syncClones(clonesNeeded);

      this.style.setProperty('--koji-shift', contentWidth + 'px');
      this.style.setProperty('--koji-duration', contentWidth / this.speed + 's');
      this.dataset.ready = 'true';
    }

    syncClones(needed) {
      const clones = this.track.querySelectorAll('.koji-marquee-clone');
      for (let i = clones.length - 1; i >= needed; i--) clones[i].remove();
      for (let i = clones.length; i < needed; i++) this.track.appendChild(this.createClone());
    }

    createClone() {
      const clone = this.original.cloneNode(true);
      clone.classList.remove('koji-marquee-content-original');
      clone.classList.add('koji-marquee-clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('img').forEach((img) => img.setAttribute('alt', ''));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
        el.removeAttribute('data-shopify-editor-block');
      });
      return clone;
    }

    setPaused(paused) {
      this.dataset.paused = paused ? 'true' : 'false';
      this.updateToggle();
    }

    updateToggle() {
      if (!this.toggleButton) return;
      const paused = this.dataset.paused === 'true';
      const label = paused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause;
      if (label) this.toggleButton.setAttribute('aria-label', label);
    }
  }

  customElements.define('koji-marquee', KojiMarquee);
})();
